/*
Product Form
*/

class ProductForm extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants || "[]");
      this.sellingPlanGroups = JSON.parse(this.dataset.sellingPlans || "[]");
      this.productOptions = JSON.parse(this.dataset["productOptions"]);
      this.defaultPurchaseOption = this.dataset.defaultPurchaseOption;
      this.subscriptionProduct = JSON.parse(
        this.dataset.subscriptionProduct || "null"
      );
      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
    } catch (e) {
      console.error("Invalid dataset", e);
      return;
    }

    this.initSelectedVariantFromUrl();
    this.initOptionListeners();
    this.initQuantityListeners();
    this.initAddToCart();

    // Ensure the default purchase option is actually selected in the DOM
    if (this.defaultPurchaseOption === "subscribe") {
      const subscribeInput = this.querySelector(
        'input[name^="purchase_option_"][value="subscribe"]'
      );
      if (subscribeInput) {
        subscribeInput.checked = true;
      }
    }

    // This will now see the correct purchase option and sync everything
    this.handleOptionChange();

    // Explicitly ensure hidden inputs and prices are correct on load
    this.updateHiddenInputs();
    this.updatePrices();
  }

  initAddToCart() {
    const form = this.querySelector("form");
    if (!form) return;
    const atcButton = form.querySelector("button[type='submit']");
    if (!atcButton) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      atcButton.disabled = true;
      atcButton.innerHTML = "Adding...";

      const variantInput = this.querySelector('input[name="id"]');
      const quantityInput = this.querySelector('input[name="quantity"]');
      const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
      const subscriptionIdInput = this.querySelector(
        'input[name="subscription_id"]'
      );
      const subscriptionSellingPlanInput = this.querySelector(
        'input[name="subscription_selling_plan"]'
      );
      const purchaseOption = this.querySelector(
        'input[name="purchase_option"]:checked'
      )?.value;

      if (!variantInput || !quantityInput) return;

      const quantity = parseInt(quantityInput.value, 10);
      const items = [];

      items.push({
        id: variantInput.value,
        quantity: quantity,
        ...(sellingPlanInput?.value
          ? { selling_plan: sellingPlanInput.value }
          : {}),
      });

      if (
        purchaseOption === "subscribe" &&
        subscriptionIdInput?.value &&
        subscriptionSellingPlanInput?.value
      ) {
        items.push({
          id: subscriptionIdInput.value,
          quantity: quantity,
          selling_plan: subscriptionSellingPlanInput.value,
        });
      }

      try {
        for (const item of items) {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [item] }),
          });

          if (!response.ok)
            throw new Error(`Add to cart failed: ${response.statusText}`);
        }

        // TVSquared action tracking
        if (window.tvqTrackAction) {
          var productTitle = document.querySelector('h1')?.textContent?.trim() || '';
          var price = String((this.selectedVariant?.price || 0) / 100);

          window.tvqTrackAction('add_to_cart', {
            'rev': '0', 'prod': productTitle, 'id': String(variantInput.value), 'promo': ''
          });
          window.tvqTrackAction('checkout_started', {
            'rev': price, 'prod': productTitle, 'id': '', 'promo': ''
          });
          if (purchaseOption === "subscribe") {
            window.tvqTrackAction('subscription_signup', {
              'rev': price, 'prod': productTitle, 'id': String(variantInput.value), 'promo': ''
            });
          }
        }

        window.location.href = "/checkout";
      } catch (err) {
        console.error("Error adding to cart", err);
        alert("Something went wrong while adding to cart. Please try again.");
        atcButton.disabled = false;
        atcButton.innerHTML = "Buy Now";
      }
    });
  }

  initQuantityListeners() {
    const quantityEl = this.querySelector(".quantity-js");
    if (!quantityEl) return;
    const plusBtn = this.querySelector(".quantity-plus-js");
    const minusBtn = this.querySelector(".quantity-minus-js");

    if (plusBtn) {
      plusBtn.addEventListener("click", () => {
        let quantity = parseInt(quantityEl.textContent.trim()) || 1;
        quantity++;
        quantityEl.textContent = quantity;
        this.updateQuantityInput(quantity);
      });
    }

    if (minusBtn) {
      minusBtn.addEventListener("click", () => {
        let quantity = parseInt(quantityEl.textContent.trim()) || 1;
        quantity = Math.max(1, quantity - 1);
        quantityEl.textContent = quantity;
        this.updateQuantityInput(quantity);
      });
    }
  }

  updateQuantityInput(quantity) {
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (quantityInput) quantityInput.value = quantity;
  }

  formatMoney(cents) {
    const amount = (cents || 0) / 100;
    const with2 = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const noDec = amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, with2)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, with2)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
  }

  updatePrices() {
    const onetimePriceEl = this.querySelector("#onetime-price");
    if (onetimePriceEl && this.selectedVariant) {
      onetimePriceEl.textContent = this.formatMoney(this.selectedVariant.price);
    }

    const subscriptionPriceEl = this.querySelector("#subscription-price");
    const subscriptionDiscountEl = this.querySelector("#subscription-discount");
    const subscriptionLabel = this.querySelector("#subscription-label");
    const planSelector = this.querySelector("#subscription-plan");

    const priceEls = document.querySelectorAll(".price-js");
    if (priceEls.length > 0) {
      const purchaseOption = this.querySelector(
        'input[name^="purchase_option_"]:checked'
      )?.value;

      let activePriceCents = this.selectedVariant.price;

      if (!this.subscriptionProduct && purchaseOption === "subscribe") {
        const selectedPlanId = parseInt(planSelector.value);
        const allocation = this.selectedVariant.selling_plan_allocations?.find(
          (a) => a.selling_plan_id == selectedPlanId
        );
        if (allocation) {
          activePriceCents = allocation.price_adjustments[0]?.price;
        }
      }

      const formattedPrice = this.formatMoney(activePriceCents);
      priceEls.forEach((el) => {
        el.textContent = formattedPrice;
      });
    }

    if (!planSelector || !subscriptionPriceEl) return;

    if (this.subscriptionProduct) {
      let subVariant = this.subscriptionProduct.variants[0];
      if (planSelector.value) {
        const matchingVariant = this.subscriptionProduct.variants.find(
          (variant) =>
            variant.selling_plan_allocations?.some(
              (allocation) => allocation.selling_plan_id == planSelector.value
            )
        );
        if (matchingVariant) subVariant = matchingVariant;
      }

      subscriptionPriceEl.innerHTML = `<s class="opacity-50">${this.formatMoney(subVariant.price)}</s> <span>${this.formatMoney(0)}</span>`;

      if (subscriptionLabel) {
        subscriptionLabel.textContent = "Subscribe";
      }
    } else if (this.selectedVariant?.selling_plan_allocations?.length > 0) {
      const availablePlans = this.selectedVariant.selling_plan_allocations.map(
        (alloc) => alloc.selling_plan_id
      );

      [...planSelector.options].forEach((opt) => {
        if (!availablePlans.includes(parseInt(opt.value))) {
          opt.style.display = "none";
        } else {
          opt.style.display = "";
        }
      });

      if (!availablePlans.includes(parseInt(planSelector.value))) {
        planSelector.value = availablePlans[0];
      }

      this.updateSubscriptionPlanDisplay();

      const selectedPlanId = parseInt(planSelector.value);
      const allocation = this.selectedVariant.selling_plan_allocations.find(
        (a) => a.selling_plan_id == selectedPlanId
      );

      if (allocation) {
        const fullPrice = this.selectedVariant.price / 100;
        const discountedPrice = allocation.price_adjustments[0]?.price / 100;
        const discountPercent = Math.round(
          (1 - discountedPrice / fullPrice) * 100
        );

        if (discountPercent > 0) {
          subscriptionPriceEl.innerHTML = `<s class="opacity-50">${this.formatMoney(fullPrice * 100)}</s> <span>${this.formatMoney(discountedPrice * 100)}</span>`;
          if (subscriptionLabel) {
            subscriptionLabel.textContent = `Subscribe & Save ${discountPercent}%`;
          }
        } else {
          subscriptionPriceEl.innerHTML = this.formatMoney(discountedPrice * 100);
          if (subscriptionLabel) {
            subscriptionLabel.textContent = `Subscribe`;
          }
        }
      }
    }
  }

  initSelectedVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = parseInt(urlParams.get("variant"), 10);
    if (variantId && this.variants.some((v) => v.id === variantId)) {
      this.selectedVariant = this.variants.find((v) => v.id === variantId);
    } else {
      this.selectedVariant =
        this.variants.find((v) => v.available) || this.variants[0];
      this.updateUrlWithVariant(this.selectedVariant.id);
    }
  }

  initOptionListeners() {
    this.querySelectorAll('input[type="radio"][name^="option-"]').forEach(
      (input) => {
        input.addEventListener("change", () => this.handleOptionChange());
      }
    );

    this.querySelectorAll('input[name^="purchase_option_"]').forEach(
      (option) => {
        option.addEventListener("change", () => {
          this.updateHiddenInputs();
          this.updatePrices();
          this.updateSubscriptionPlanDisplay();
        });
      }
    );

    const planSelector = this.querySelector("#subscription-plan");
    if (planSelector) {
      planSelector.addEventListener("change", () => {
        this.updateHiddenInputs();
        this.updatePrices();
        this.updateSubscriptionPlanDisplay();
      });
    }
  }

  updateSubscriptionPlanDisplay() {
    const planSelector = this.querySelector("#subscription-plan");
    const planDisplay = this.querySelector("#subscription-plan-display");
    if (!planSelector || !planDisplay) return;

    const selectedOption = planSelector.options[planSelector.selectedIndex];
    if (selectedOption) {
      planDisplay.textContent = selectedOption.textContent;
    }
  }

  handleOptionChange() {
    const labelEl = this.querySelector(".variant-label-js");
    const selectedOptions = [];
    const optionGroups = [
      ...new Set(
        [...this.querySelectorAll('input[type="radio"][name^="option-"]')].map(
          (input) => input.name
        )
      ),
    ];

    optionGroups.forEach((groupName) => {
      const selectedInput = this.querySelector(
        `input[name="${groupName}"]:checked`
      );
      if (selectedInput) selectedOptions.push(selectedInput.value);
    });

    const matchingVariant = this.variants.find((variant) => {
      return variant.options.every((opt, idx) => opt === selectedOptions[idx]);
    });

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;
      this.updateUrlWithVariant(matchingVariant.id);
      this.updateActiveStates();

      const planSelector = this.querySelector("#subscription-plan");
      if (planSelector && !this.subscriptionProduct) {
        const availablePlans =
          this.selectedVariant.selling_plan_allocations.map(
            (alloc) => alloc.selling_plan_id
          );

        [...planSelector.options].forEach((opt) => {
          if (!availablePlans.includes(parseInt(opt.value))) {
            opt.style.display = "none";
          } else {
            opt.style.display = "";
          }
        });

        if (!availablePlans.includes(parseInt(planSelector.value))) {
          planSelector.value = availablePlans[0];
        }
      }
    }

    // Always sync hidden inputs and prices, even if variant did not change
    this.updateHiddenInputs();
    this.updatePrices();

    if (labelEl) {
      const colorOption = this.productOptions?.find(
        (option) => option.name === "Color"
      );

      if (!!colorOption) {
        labelEl.textContent = this.selectedVariant.title;
      }
    }
  }

  updateHiddenInputs() {
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = this.selectedVariant?.id || "";
    }

    const purchaseOption = this.querySelector(
      'input[name^="purchase_option_"]:checked'
    );

    const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    const subscriptionIdInput = this.querySelector(
      'input[name="subscription_id"]'
    );
    const subscriptionSellingPlanInput = this.querySelector(
      'input[name="subscription_selling_plan"]'
    );
    const planSelector = this.querySelector("#subscription-plan");

    if (purchaseOption?.value === "subscribe") {
      if (this.subscriptionProduct) {
        const selectedSellingPlanId = planSelector.value;
        const matchingVariant = this.subscriptionProduct.variants.find(
          (variant) =>
            variant.selling_plan_allocations?.some(
              (allocation) =>
                allocation.selling_plan_id == selectedSellingPlanId
            )
        );

        if (subscriptionIdInput && matchingVariant) {
          subscriptionIdInput.value = matchingVariant.id;
        } else if (subscriptionIdInput) {
          subscriptionIdInput.value = "";
        }

        if (subscriptionSellingPlanInput) {
          subscriptionSellingPlanInput.value = selectedSellingPlanId;
        }

        if (sellingPlanInput) {
          sellingPlanInput.value = "";
        }
      } else {
        if (planSelector && sellingPlanInput) {
          const selectedSellingPlanId = parseInt(planSelector.value);
          const allocation =
            this.selectedVariant.selling_plan_allocations?.find(
              (alloc) => alloc.selling_plan_id == selectedSellingPlanId
            );
          if (allocation) {
            sellingPlanInput.value = selectedSellingPlanId;
          } else {
            sellingPlanInput.value = "";
          }
        }

        if (subscriptionIdInput) {
          subscriptionIdInput.value = "";
        }
        if (subscriptionSellingPlanInput) {
          subscriptionSellingPlanInput.value = "";
        }
      }
    } else {
      if (sellingPlanInput) {
        sellingPlanInput.value = "";
      }
      if (subscriptionIdInput) {
        subscriptionIdInput.value = "";
      }
      if (subscriptionSellingPlanInput) {
        subscriptionSellingPlanInput.value = "";
      }
    }
  }

  updateActiveStates() {
    this.querySelectorAll(".option").forEach((el) =>
      el.classList.remove("active")
    );
    const optionGroups = [
      ...new Set(
        [...this.querySelectorAll('input[type="radio"][name^="option-"]')].map(
          (input) => input.name
        )
      ),
    ];
    optionGroups.forEach((groupName) => {
      const selectedInput = this.querySelector(
        `input[name="${groupName}"]:checked`
      );
      if (selectedInput)
        selectedInput.previousElementSibling?.classList.add("active");
    });
  }

  updateUrlWithVariant(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.pushState({}, "", url.toString());
  }
}

customElements.define("product-form", ProductForm);

/*
Thumbnail Scroller
*/

class ThumbnailScroller extends HTMLElement {
  constructor() {
    super();
    this.buffer = 10;
    this.scrollUp = this.scrollUp.bind(this);
    this.scrollDown = this.scrollDown.bind(this);
    this.checkScrollArrows = this.checkScrollArrows.bind(this);
  }

  connectedCallback() {
    // .thumbnails-container is the legacy pdp-gallery.liquid's class name;
    // pdp-gallery-v2.liquid (the one actually in use) names this element
    // .pdp-gallery-v2__thumbs-list instead. Matching both keeps this
    // component working for either gallery markup.
    this.thumbnailContainer = this.querySelector(
      ".thumbnails-container, .pdp-gallery-v2__thumbs-list"
    );
    this.scrollUpArrow = this.querySelector(".scroll-up-arrow");
    this.scrollDownArrow = this.querySelector(".scroll-down-arrow");

    if (this.scrollUpArrow) {
      this.scrollUpArrow.addEventListener("click", this.scrollUp);
    }
    if (this.scrollDownArrow) {
      this.scrollDownArrow.addEventListener("click", this.scrollDown);
    }
    if (this.thumbnailContainer) {
      this.thumbnailContainer.addEventListener(
        "scroll",
        this.checkScrollArrows
      );
      window.addEventListener("DOMContentLoaded", this.checkScrollArrows);
      this.checkScrollArrows();
    }
  }

  disconnectedCallback() {
    if (this.scrollUpArrow) {
      this.scrollUpArrow.removeEventListener("click", this.scrollUp);
    }
    if (this.scrollDownArrow) {
      this.scrollDownArrow.removeEventListener("click", this.scrollDown);
    }
    if (this.thumbnailContainer) {
      this.thumbnailContainer.removeEventListener(
        "scroll",
        this.checkScrollArrows
      );
      window.removeEventListener("DOMContentLoaded", this.checkScrollArrows);
    }
  }

  scrollDown() {
    this.thumbnailContainer.scrollBy({
      top: this.thumbnailContainer.clientHeight,
      behavior: "smooth",
    });
  }

  scrollUp() {
    this.thumbnailContainer.scrollBy({
      top: -this.thumbnailContainer.clientHeight,
      behavior: "smooth",
    });
  }

  checkScrollArrows() {
    const scrollTop = this.thumbnailContainer.scrollTop;
    const scrollHeight = this.thumbnailContainer.scrollHeight;
    const clientHeight = this.thumbnailContainer.clientHeight;

    if (this.scrollUpArrow) {
      if (scrollTop > 0) {
        this.scrollUpArrow.classList.remove("opacity-0", "invisible");
      } else {
        this.scrollUpArrow.classList.add("opacity-0", "invisible");
      }
    }

    if (this.scrollDownArrow) {
      if (
        scrollHeight > clientHeight &&
        scrollTop + clientHeight < scrollHeight - this.buffer
      ) {
        this.scrollDownArrow.classList.remove("opacity-0", "invisible");
      } else {
        this.scrollDownArrow.classList.add("opacity-0", "invisible");
      }
    }
  }
}

customElements.define("thumbnail-scroller", ThumbnailScroller);

class ProductGallery extends HTMLElement {
  constructor() {
    super();

    this.currentVariantId = "";
  }

  connectedCallback() {
    this.setupVariantChangeListeners();
  }

  setupVariantChangeListeners() {
    const optionInputs = document.querySelectorAll(
      'product-form input[type="radio"][name^="option-"]'
    );

    optionInputs.forEach((input) => {
      input.addEventListener("change", () => {
        this.updateSelectedVariantId();
        this.updateGalleryImages();
      });
    });
  }

  updateSelectedVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantFromUrl = urlParams.get("variant");

    this.currentVariantId = variantFromUrl ? Number(variantFromUrl) : null;
  }

  updateGalleryImages() {
    if (!this.currentVariantId) return;

    const allImages = this.querySelectorAll("[data-variant-main-image]");
    const imagesForVariant = this.querySelectorAll(
      `[data-variant-id='${this.currentVariantId}']`
    );
    const carrouselPager = this.querySelector("carousel-pager");

    allImages.forEach((image) => {
      image.classList.add("hidden");
    });

    imagesForVariant.forEach((image) => {
      image.classList.remove("hidden");
    });

    carrouselPager && carrouselPager.pagerButtons();
  }
}

customElements.define("product-gallery", ProductGallery);
